import React, { useState, useEffect } from 'react';
import { FileText, Filter, Download, AlertCircle, Info, AlertTriangle, XCircle } from 'lucide-react';
import api from '../../services/api';

const SMPPLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    event_type: '',
    severity: '',
    connector_id: '',
    start_date: '',
    end_date: '',
    limit: 100
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/super/smpp/logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setLoading(true);
    fetchLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      event_type: '',
      severity: '',
      connector_id: '',
      start_date: '',
      end_date: '',
      limit: 100
    });
    setTimeout(() => {
      setLoading(true);
      fetchLogs();
    }, 100);
  };

  const getSeverityBadge = (severity) => {
    const severityMap = {
      ERROR: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
      WARNING: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: AlertTriangle },
      INFO: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Info },
      SUCCESS: { bg: 'bg-green-100', text: 'text-green-800', icon: AlertCircle }
    };

    const config = severityMap[severity] || severityMap.INFO;
    const Icon = config.icon;

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.bg} ${config.text} flex items-center gap-1 w-fit`}>
        <Icon className="w-3 h-3" />
        {severity}
      </span>
    );
  };

  const getEventTypeBadge = (eventType) => {
    const typeColors = {
      BIND: 'bg-blue-100 text-blue-800',
      UNBIND: 'bg-gray-100 text-gray-800',
      SUBMIT: 'bg-green-100 text-green-800',
      SUBMIT_ERROR: 'bg-red-100 text-red-800',
      BIND_FAILED: 'bg-red-100 text-red-800',
      THROTTLE: 'bg-yellow-100 text-yellow-800',
      ENQUIRE_LINK: 'bg-purple-100 text-purple-800',
      CONNECTION: 'bg-indigo-100 text-indigo-800'
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${typeColors[eventType] || 'bg-gray-100 text-gray-800'}`}>
        {eventType}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">SMPP Logs & Audit</h1>
          <p className="text-gray-600 mt-2">View operational logs and audit trail</p>
        </div>
        <button
          onClick={() => window.location.href = '/super/smpp'}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          ← Back to SMPP Management
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <select
              value={filters.event_type}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Events</option>
              <option value="BIND">BIND</option>
              <option value="UNBIND">UNBIND</option>
              <option value="SUBMIT">SUBMIT</option>
              <option value="SUBMIT_ERROR">SUBMIT_ERROR</option>
              <option value="BIND_FAILED">BIND_FAILED</option>
              <option value="THROTTLE">THROTTLE</option>
              <option value="ENQUIRE_LINK">ENQUIRE_LINK</option>
              <option value="CONNECTION">CONNECTION</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Levels</option>
              <option value="ERROR">ERROR</option>
              <option value="WARNING">WARNING</option>
              <option value="INFO">INFO</option>
              <option value="SUCCESS">SUCCESS</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Connector ID</label>
            <input
              type="text"
              value={filters.connector_id}
              onChange={(e) => handleFilterChange('connector_id', e.target.value)}
              placeholder="Enter ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Connector
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Error Code
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getEventTypeBadge(log.event_type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getSeverityBadge(log.severity)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.connector_name || `ID: ${log.connector_id}`}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                    {log.message}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.error_code || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {logs.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow mt-6">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No logs found matching the current filters</p>
        </div>
      )}

      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-gray-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-1">
              Log Retention Policy
            </h4>
            <p className="text-sm text-gray-700">
              SMPP logs are retained for 30 days. Use filters to narrow down results for faster searches.
              Critical errors are automatically flagged and can trigger alerts based on configuration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMPPLogs;
